<template>
  <div class="dash-card top-students-widget">
    <div class="dash-card-header">
      <h3 class="dash-card-title">
        <span
          class="dash-card-title-icon"
          style="background: #fff7e6; color: #faad14"
        >
          <a-icon type="trophy" />
        </span>
        {{ $t("accueil.dashboard.topStudents") }}
      </h3>
      <div class="top-students-toolbar">
        <a-select
          v-model="bulletinType"
          size="small"
          class="top-students-type-select"
          @change="loadTopStudents"
        >
          <a-select-option
            v-for="opt in bulletinTypeOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </a-select-option>
        </a-select>
        <a-select
          v-model="trimester"
          size="small"
          class="top-students-trimester"
          @change="loadTopStudents"
        >
          <a-select-option v-for="t in ['1', '2', '3']" :key="t" :value="t">
            T{{ t }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <div class="dash-card-body">
      <div v-if="loading" class="top-students-loading">
        <a-spin />
      </div>
      <div v-else-if="students.length" class="top-students-list dash-scroll">
        <div
          v-for="(student, index) in students"
          :key="student._id || index"
          class="top-student-row"
        >
          <span class="rank-badge" :class="rankClass(index)">{{
            index + 1
          }}</span>

          <img
            v-if="photoUrl(student)"
            class="top-student-avatar"
            :src="photoUrl(student)"
            alt=""
          />
          <span v-else class="top-student-avatar top-student-avatar--initials">
            {{ initialsFor(student) }}
          </span>

          <div class="top-student-info">
            <div class="top-student-name">{{ displayName(student) }}</div>
            <div class="top-student-class">
              <a-icon type="team" />
              <span>{{ student.classRoomName || "—" }}</span>
              <template v-if="levelLabel(student)">
                <span class="top-student-sep">·</span>
                <span dir="ltr">{{ levelLabel(student) }}</span>
              </template>
            </div>
          </div>

          <div class="top-student-score">
            <span class="score-value">{{ formatMoyenne(student.moyenne) }}</span>
            <span class="score-label">{{
              $t("accueil.dashboard.moyenne")
            }}</span>
          </div>
        </div>
      </div>
      <div v-else class="dash-empty-state top-students-empty">
        <div class="dash-empty-icon">
          <a-icon type="trophy" />
        </div>
        <div>{{ $t("accueil.dashboard.noTopStudents") }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { getAllLevels } from "@/config/LevelConstants";

export default {
  name: "TopStudentsWidget",
  data() {
    return {
      loading: false,
      bulletinType: "MP",
      trimester: "1",
      students: [],
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      const buildings = (this.user && this.user.building) || [];
      return buildings.find(
        (el) => el.dbName == (this.settings && this.settings.activeBuilding)
      );
    },
    countryCode() {
      return (this.activeBuilding && this.activeBuilding.countryCode) || "TN";
    },
    allLevels() {
      return getAllLevels(this.$t.bind(this), this.countryCode);
    },
    bulletinTypeOptions() {
      return [
        {
          value: "MP",
          label: this.$t("accueil.dashboard.bulletinPedagogique"),
        },
        {
          value: "MT",
          label: this.$t("accueil.dashboard.bulletinPilote"),
        },
        {
          value: "MS",
          label: this.$t("accueil.dashboard.bulletinSpecifique"),
        },
      ];
    },
  },
  created() {
    this.loadTopStudents();
  },
  methods: {
    async loadTopStudents() {
      this.loading = true;
      try {
        const { data } = await apiClient.get(
          `/bulletin/v2/pv/level_all/${this.bulletinType}/${this.trimester}`
        );
        this.students = this.extractTop10(data);
      } catch (error) {
        console.error("Error loading top students:", error);
        this.students = [];
      } finally {
        this.loading = false;
      }
    },
    extractTop10(levelData) {
      const levels = Array.isArray(levelData) ? levelData : [];
      const all = [];
      levels.forEach((levelBlock) => {
        const list = (levelBlock && levelBlock.students) || [];
        list.forEach((student) => {
          const moyenne = this.parseMoyenne(student && student.moyenne);
          if (moyenne == null) return;
          all.push({
            _id: student.id || student._id,
            fullName: student.fullName || "",
            firstName: student.firstName || "",
            lastName: student.lastName || "",
            classRoomName: student.classroomName || "",
            level:
              student.classroomLevel != null
                ? student.classroomLevel
                : levelBlock.level,
            photo: student.photo || null,
            moyenne,
          });
        });
      });
      return all.sort((a, b) => b.moyenne - a.moyenne).slice(0, 10);
    },
    parseMoyenne(value) {
      if (value == null || value === "" || value === "--") return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    },
    formatMoyenne(value) {
      return Number(value).toFixed(2);
    },
    displayName(student) {
      if (student.fullName) return student.fullName;
      return (
        `${student.firstName || ""} ${student.lastName || ""}`.trim() || "—"
      );
    },
    levelLabel(student) {
      if (
        student.level === null ||
        student.level === undefined ||
        student.level === ""
      ) {
        return "";
      }
      return this.allLevels[String(student.level)] || String(student.level);
    },
    photoUrl(student) {
      const photo = student && student.photo;
      if (!photo || typeof photo !== "string") return null;
      if (photo.startsWith("http")) return photo;
      const base = this.settings && this.settings.resourcesUrl;
      if (!base) return null;
      return base.replace("FILE_PATH", photo);
    },
    initialsFor(student) {
      if (student.fullName) {
        const parts = student.fullName.trim().split(/\s+/);
        const a = (parts[0] || "").charAt(0);
        const b = (parts[parts.length - 1] || "").charAt(0);
        return `${a}${b}`.toUpperCase() || "?";
      }
      const a = (student.firstName || "").trim().charAt(0);
      const b = (student.lastName || "").trim().charAt(0);
      return `${a}${b}`.toUpperCase() || "?";
    },
    rankClass(index) {
      if (index === 0) return "rank-gold";
      if (index === 1) return "rank-silver";
      if (index === 2) return "rank-bronze";
      return "rank-other";
    },
  },
};
</script>

<style lang="scss" scoped>
.dash-card-header {
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.top-students-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.top-students-type-select {
  min-width: 130px;
}

.top-students-trimester {
  width: 68px;
  flex-shrink: 0;
}

.top-students-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
}

.top-students-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 160px;
}

.top-students-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 380px;
}

.top-student-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 6px;
  border-radius: 10px;
  transition: background 0.18s ease;

  &:hover {
    background: #fafafa;
  }
}

.rank-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;

  &.rank-gold {
    background: #faad14;
    color: #fff;
  }
  &.rank-silver {
    background: #a1a1c2;
    color: #fff;
  }
  &.rank-bronze {
    background: #d46b08;
    color: #fff;
  }
  &.rank-other {
    background: #f0f0f0;
    color: #595959;
  }
}

.top-student-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;

  &--initials {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    background: #fff7e6;
    color: #d48806;
  }
}

.top-student-info {
  flex: 1;
  min-width: 0;
}

.top-student-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-student-class {
  font-size: 0.72rem;
  color: #8c8c8c;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.top-student-sep {
  opacity: 0.6;
}

.top-student-score {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;

  .score-value {
    font-size: 1rem;
    font-weight: 800;
    color: var(--dash-success);
  }

  .score-label {
    font-size: 0.62rem;
    color: #8c8c8c;
    text-transform: uppercase;
  }
}

[data-kit-theme="dark"] {
  .top-student-row:hover {
    background: #141322;
  }

  .top-student-name {
    color: #aeaee0;
  }

  .top-student-avatar--initials {
    background: rgba(250, 173, 20, 0.18);
    color: #faad14;
  }

  .rank-badge.rank-other {
    background: #232135;
    color: #aeaee0;
  }
}
</style>
